import { sample } from 'effector';
import { spread } from 'patronum';
import {
  $absoluteInfoData,
  $absoluteLayers,
  $conPoints,
  $conStartPoints,
  $faPoints,
  $faStartPoints,
  $gcPoints,
  $gcStartPoints,
  $hosPoints,
  $hosStartPoints,
  $isAbsolute,
  $kgPoints,
  $kgStartPoints,
  $stPoints,
  $stStartPoints,
  $scPoints,
  $scStartPoints,
  $fc_ssPoints,
  $fc_ssStartPoints,
  $polPoints,
  $polStartPoints,
} from './stores.js';
import {
  changeAbsoluteLayersEv,
  changeIsAbsoluteEv,
  putRawDataEv,
  resetAbsoluteLayersEv,
} from './events.js';
import { changeIndexTreeTabEv } from '../indexCardsModel/index.js';
import { $zoom10Hexagons } from '../zoom10Model/index.js';
import { makePoint } from '../../utils/mapbox-utils.js';
import { $activeFilters } from '../activeFiltersModel/index.js';
import { hasActiveFilters } from '../../utils/active-filters-utils.js';
import filterHexagons from '../../counters/hex_counter/hex_counter.js';

$isAbsolute.on(changeIsAbsoluteEv, (state, payload) => {
  if (typeof payload === 'boolean') {
    return payload;
  }
  return !state;
});

$absoluteLayers.reset(resetAbsoluteLayersEv);

sample({
  clock: changeIndexTreeTabEv,
  fn: (clock) => clock === 'absolute',
  target: changeIsAbsoluteEv,
});

sample({
  clock: changeAbsoluteLayersEv,
  target: $absoluteLayers,
});

sample({
  source: [
    $zoom10Hexagons,
    $kgPoints,
    $scPoints,
    $stPoints,
    $fc_ssPoints,
    $gcPoints,
    $hosPoints,
    $polPoints,
    $conPoints,
    $faPoints,
  ],
  clock: $absoluteLayers.updates,
  fn: (
    [
      zoom10Hexagons,
      kgPoints,
      scPoints,
      stPoints,
      fc_ssPoints,
      gcPoints,
      hosPoints,
      polPoints,
      conPoints,
      faPoints,
    ],
    clock
  ) => {
    const points_dict = {
      kg: kgPoints,
      sc: scPoints,
      st: stPoints,
      fc_ss: fc_ssPoints,
      gc: gcPoints,
      hos: hosPoints,
      pol: polPoints,
      con: conPoints,
      fa: faPoints,
    };

    return clock.map((item, index) => {
      let value;
      if (item.type === 'points') {
        value = points_dict[item.name].length;
      } else {
        value = zoom10Hexagons
          .map((elem) => elem.properties[item.name])
          .reduce((a, b) => a + b, 0);
      }
      return {
        key: index,
        label: item.title,
        children: Math.round(value),
      };
    });
  },
  target: $absoluteInfoData,
});

sample({
  clock: putRawDataEv,
  fn: (clock) => {
    const kgPoints = clock.kids_garden.map((item) => {
      return makePoint(item, [item.latitude, item.longitude]);
    });
    const scPoints = clock.schools.map((item) => {
      return makePoint(item, [item.latitude, item.longitude]);
    });
    const stPoints = clock.stops.map((item) => {
      return makePoint(item, [item.lon, item.lat]);
    });
    const fc_ssPoints = [];
    const gcPoints = [];
    const hosPoints = [];
    const polPoints = [];
    const conPoints = [];
    const faPoints = [];

    clock.business.forEach((item) => {
      const point = makePoint(item, [item.lon, item.lat]);
      switch (item.category) {
        case 'Фитнес-клубы':
        case 'Спортивные секции':
          fc_ssPoints.push(point);
          break;
        case 'Мусорные контейнеры':
          gcPoints.push(point);
          break;
        case 'Больницы':
          hosPoints.push(point);
          break;
        case 'Взрослые поликлиники':
        case 'Детские поликлиники':
          polPoints.push(point);
          break;
        case 'ЦОН':
          conPoints.push(point);
          break;
        case 'Скорая медицинская помощь':
          faPoints.push(point);
          break;
        default:
          break;
      }
    });

    return {
      kgPoints,
      scPoints,
      stPoints,
      fc_ssPoints,
      gcPoints,
      hosPoints,
      polPoints,
      conPoints,
      faPoints,
    };
  },
  target: spread({
    targets: {
      kgPoints: [$kgPoints, $kgStartPoints],
      scPoints: [$scPoints, $scStartPoints],
      stPoints: [$stPoints, $stStartPoints],
      fc_ssPoints: [$fc_ssPoints, $fc_ssStartPoints],
      gcPoints: [$gcPoints, $gcStartPoints],
      hosPoints: [$hosPoints, $hosStartPoints],
      polPoints: [$polPoints, $polStartPoints],
      conPoints: [$conPoints, $conStartPoints],
      faPoints: [$faPoints, $faStartPoints],
    },
  }),
});

sample({
  clock: $isAbsolute.updates,
  filter: (clock) => !clock,
  target: resetAbsoluteLayersEv,
});

sample({
  source: [
    $kgStartPoints,
    $scStartPoints,
    $stStartPoints,
    $fc_ssStartPoints,
    $gcStartPoints,
    $hosStartPoints,
    $polStartPoints,
    $conStartPoints,
    $faStartPoints,
  ],
  clock: $activeFilters.updates,
  filter: (clock) => hasActiveFilters(clock),
  fn: (
    [
      kgPoints,
      scPoints,
      stPoints,
      fc_ssPoints,
      gcPoints,
      hosPoints,
      polPoints,
      conPoints,
      faPoints,
    ],
    clock
  ) => {
    return {
      kgPoints: filterHexagons(kgPoints, clock, [], false),
      scPoints: filterHexagons(scPoints, clock, [], false),
      stPoints: filterHexagons(stPoints, clock, [], false),
      fc_ssPoints: filterHexagons(fc_ssPoints, clock, [], false),
      gcPoints: filterHexagons(gcPoints, clock, [], false),
      hosPoints: filterHexagons(hosPoints, clock, [], false),
      polPoints: filterHexagons(polPoints, clock, [], false),
      conPoints: filterHexagons(conPoints, clock, [], false),
      faPoints: filterHexagons(faPoints, clock, [], false),
    };
  },
  target: spread({
    targets: {
      kgPoints: $kgPoints,
      scPoints: $scPoints,
      stPoints: $stPoints,
      fc_ssPoints: $fc_ssPoints,
      gcPoints: $gcPoints,
      hosPoints: $hosPoints,
      polPoints: $polPoints,
      conPoints: $conPoints,
      faPoints: $faPoints,
    },
  }),
});

sample({
  source: [
    $kgStartPoints,
    $scStartPoints,
    $stStartPoints,
    $fc_ssStartPoints,
    $gcStartPoints,
    $hosStartPoints,
    $polStartPoints,
    $conStartPoints,
    $faStartPoints,
  ],
  clock: $activeFilters.updates,
  filter: (clock) => !hasActiveFilters(clock),
  fn: (
    [
      kgPoints,
      scPoints,
      stPoints,
      fc_ssPoints,
      gcPoints,
      hosPoints,
      polPoints,
      conPoints,
      faPoints,
    ],
    clock
  ) => {
    return {
      kgPoints,
      scPoints,
      stPoints,
      fc_ssPoints,
      gcPoints,
      hosPoints,
      polPoints,
      conPoints,
      faPoints,
    };
  },
  target: spread({
    targets: {
      kgPoints: $kgPoints,
      scPoints: $scPoints,
      stPoints: $stPoints,
      fc_ssPoints: $fc_ssPoints,
      gcPoints: $gcPoints,
      hosPoints: $hosPoints,
      polPoints: $polPoints,
      conPoints: $conPoints,
      faPoints: $faPoints,
    },
  }),
});
